import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  GetCmpWhatsNewIndexDocument,
  useGetCmpWhatsNewIndexQuery,
  useMarkCmpWhatsNewSeenMutation,
} from "../../../graphql/types";
import type { CmpNavbarGroup, CmpNavbarItem } from "../../Navbar/cmpNavbarPresentation";
import { WHATS_NEW_GUIDE_CONTEXT } from "./CmpWhatsNew";

// Sidebar "Updated" pills, red dots and the What's New view (Monday
// 13282310441), following Claude Design CMP Dashboard (19) navUpdateShown(),
// markNavSeen() and the sb.*.go handlers:
//  - module header: "Updated" when any of its pages has an update;
//  - page: 6px red dot (Calculators: "New"), never on Coming Soon items;
//  - clicking a page with an update opens it with What's New on top, and
//    reaching that page clears its pill for this user (server side);
//  - pills show for 30 days from the live publish (decided by the backend).

export type CmpWhatsNewEntry = { key: string; name: string; date: string; updated: boolean };

/** Keys with an update to show: backend "updated" minus pages this user just opened. */
export const updatedWhatsNewKeys = (entries: readonly CmpWhatsNewEntry[], locallySeen: ReadonlySet<string>): Set<string> =>
  new Set(entries.filter((entry) => entry.updated && !locallySeen.has(entry.key)).map((entry) => entry.key));

export const itemHasUpdate = (item: CmpNavbarItem, updated: ReadonlySet<string>): boolean =>
  Boolean(item.whatsNewKey && !item.badge && updated.has(item.whatsNewKey));

export const groupHasUpdate = (group: CmpNavbarGroup, updated: ReadonlySet<string>): boolean =>
  group.items.some((item) => itemHasUpdate(item, updated));

export const useCmpWhatsNewNav = ({ activeItem, locationSignature }: { activeItem: CmpNavbarItem | null; locationSignature: string }) => {
  const { data } = useGetCmpWhatsNewIndexQuery({ fetchPolicy: "cache-and-network", context: WHATS_NEW_GUIDE_CONTEXT });
  const [markSeenMutation] = useMarkCmpWhatsNewSeenMutation({
    refetchQueries: [{ query: GetCmpWhatsNewIndexDocument, context: WHATS_NEW_GUIDE_CONTEXT }],
    context: WHATS_NEW_GUIDE_CONTEXT,
  });
  const [locallySeen, setLocallySeen] = useState<Set<string>>(() => new Set());
  const [openKey, setOpenKey] = useState<string | null>(null);
  const pendingRef = useRef<{ itemId: string; key: string } | null>(null);
  const openItemIdRef = useRef<string | null>(null);
  const entries = useMemo(() => data?.getCmpWhatsNewIndex.entries || [], [data]);
  const updated = useMemo(() => updatedWhatsNewKeys(entries, locallySeen), [entries, locallySeen]);

  const markSeen = useCallback((key: string) => {
    setLocallySeen((current) => (current.has(key) ? current : new Set(current).add(key)));
    Promise.resolve(markSeenMutation({ variables: { key } })).catch(() => undefined);
  }, [markSeenMutation]);

  const open = useCallback((item: CmpNavbarItem) => {
    if (!item.whatsNewKey) return;
    openItemIdRef.current = item.id;
    setOpenKey(item.whatsNewKey);
  }, []);

  /** Sidebar click. Returns true when the click was handled here (no navigation needed). */
  const onItemClick = useCallback((item: CmpNavbarItem): boolean => {
    if (openKey && item.isActive) {
      // Design: clicking the current page while What's New is open closes it.
      setOpenKey(null);
      return true;
    }
    pendingRef.current = itemHasUpdate(item, updated) ? { itemId: item.id, key: item.whatsNewKey! } : null;
    if (pendingRef.current && item.isActive) {
      open(item);
      markSeen(item.whatsNewKey!);
      pendingRef.current = null;
      return true;
    }
    return false;
  }, [markSeen, open, openKey, updated]);

  // The page changed (sidebar, unsaved-changes guard passed, or any link).
  useEffect(() => {
    const pending = pendingRef.current;
    if (openItemIdRef.current && activeItem?.id !== openItemIdRef.current) {
      setOpenKey(null);
      openItemIdRef.current = null;
    }
    if (pending && activeItem?.id === pending.itemId) {
      open(activeItem);
      pendingRef.current = null;
    } else if (pending) {
      pendingRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs per page change, like the design componentDidUpdate
  }, [locationSignature, activeItem?.id]);

  // Reaching a page with an update clears its pill (also when the index loads after the page).
  useEffect(() => {
    if (activeItem && itemHasUpdate(activeItem, updated)) markSeen(activeItem.whatsNewKey!);
  }, [activeItem, updated, markSeen]);

  return { updated, openKey, close: () => { setOpenKey(null); openItemIdRef.current = null; }, onItemClick };
};
